import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { fdt } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ShieldCheck, Clock, Trash2, Ban, RotateCcw, Search } from "lucide-react";
import { toast } from "sonner";
import { ClinicalChangeQueue } from "@/components/pawkt/ClinicalChange";

const ACTION_META = {
  "customers.delete": { label: "Delete Customer", icon: Trash2 },
  "pets.delete": { label: "Delete Pet", icon: Trash2 },
  "bookings.delete": { label: "Delete Booking", icon: Trash2 },
  "package_plans.delete": { label: "Delete Package Plan", icon: Trash2 },
  "inventory.delete": { label: "Delete Inventory Item", icon: Trash2 },
  "invoices.cancel": { label: "Cancel Invoice", icon: Ban },
  "payments.refund": { label: "Refund Payment", icon: RotateCcw },
};
const TABS = ["pending", "approved", "rejected", "expired"];
const STATUS_TONE = {
  pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-rose-100 text-rose-700", expired: "bg-slate-200 text-slate-600",
  stale: "bg-orange-100 text-orange-700", failed: "bg-rose-100 text-rose-700",
};

function timeLeft(iso) {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / 3.6e6), m = Math.floor((ms % 3.6e6) / 6e4);
  return h >= 1 ? `${h}h left` : `${m}m left`;
}

export default function ApprovalCenter() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("pending");
  const [actionFilter, setActionFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);

  // Owner-wide center is approver-only; receptionists never reach it.
  const isApprover = !user || hasPerm(user, "approval.approve");

  const { data: summary } = useQuery({
    queryKey: ["approvals-summary"],
    queryFn: async () => (await api.get("/approvals/summary")).data,
    enabled: isApprover,
  });
  const { data, isLoading } = useQuery({
    queryKey: ["approvals", tab, actionFilter],
    queryFn: async () => (await api.get(`/approvals?status=${tab}${actionFilter !== "all" ? `&action=${actionFilter}` : ""}&limit=200`)).data,
    enabled: isApprover,
  });

  if (user && !hasPerm(user, "approval.approve")) return <Navigate to="/dashboard" replace />;

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ["approvals"] });
    qc.invalidateQueries({ queryKey: ["approvals-summary"] });
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };
  const handleConflict = (e) => {
    const d = e?.response?.data?.detail;
    const code = d?.code;
    const msg = { ALREADY_PROCESSED: "This request was already processed.", EXPIRED: "This request has expired.",
      STALE: "This item changed since the request was created. Ask for a new request.",
      SELF_APPROVAL: "You cannot approve your own request.",
      EXECUTION_FAILED: `Could not complete the action: ${typeof d?.detail === "string" ? d.detail : "please review the record."}` }[code];
    toast.error(msg || apiErr(e));
    setSel(null); refetchAll();
  };

  const items = (data?.items || []).filter((r) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (r.target_label || "").toLowerCase().includes(s) || (r.requested_by_name || "").toLowerCase().includes(s);
  });

  return (
    <div>
      <PageHeader title="Approval Center" subtitle="Review and decide sensitive requests from your team" />
      {hasPerm(user, "vet.records.approve") && <ClinicalChangeQueue />}

      <div className="flex flex-wrap gap-2 mb-4" data-testid="approval-tabs">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`approval-tab-${t}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold capitalize transition-colors ${tab === t ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {t} {summary ? <span className="ml-1 opacity-70">{summary[t] ?? 0}</span> : null}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="approval-search" placeholder="Search item or requester…"
            className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-amber-500" />
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="h-10 w-48 bg-white" data-testid="approval-action-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All actions</SelectItem>
            {Object.entries(ACTION_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? <Loading /> : (items.length ? (
        <div className="space-y-2.5" data-testid="approval-list">
          {items.map((r) => {
            const M = ACTION_META[r.action] || { label: r.action, icon: ShieldCheck };
            const Icon = M.icon;
            return (
              <button key={r.id} onClick={() => setSel(r)} data-testid={`approval-row-${r.id}`}
                className="w-full text-left rounded-xl border border-slate-200 bg-white p-4 hover:border-amber-300 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0"><Icon className="h-4 w-4" /></div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900">{M.label}</div>
                      <div className="text-sm text-slate-600 truncate">{r.target_label}</div>
                      <div className="text-xs text-slate-400 mt-0.5">By {r.requested_by_name || "—"} · {fdt(r.created_at)}{r.branch_id ? "" : ""}</div>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_TONE[r.status] || "bg-slate-100 text-slate-600"}`}>{r.status}</span>
                    {r.status === "pending" && <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="h-3 w-3" />{timeLeft(r.expires_at)}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : <Empty icon={ShieldCheck} title={`No ${tab} requests`} />)}

      {sel && <ReviewModal req={sel} onClose={() => setSel(null)} onConflict={handleConflict} refetchAll={refetchAll} />}
    </div>
  );
}

function ReviewModal({ req, onClose, onConflict, refetchAll }) {
  const [note, setNote] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [rejReason, setRejReason] = useState("");
  const M = ACTION_META[req.action] || { label: req.action };
  const pending = req.status === "pending";

  const approve = useMutation({
    mutationFn: () => api.post(`/approvals/${req.id}/approve`, { reason: note.trim() }),
    onSuccess: () => { toast.success("Request approved and executed."); onClose(); refetchAll(); },
    onError: onConflict,
  });
  const reject = useMutation({
    mutationFn: () => api.post(`/approvals/${req.id}/reject`, { reason: rejReason.trim() }),
    onSuccess: () => { toast.success("Request rejected."); onClose(); refetchAll(); },
    onError: onConflict,
  });
  const busy = approve.isPending || reject.isPending;
  const before = req.before_state || {};

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-md" data-testid="approval-review-modal">
        <DialogHeader><DialogTitle className="font-display">{M.label}</DialogTitle></DialogHeader>

        <div className="rounded-xl border border-pawkt-border bg-pawkt-bg p-3 text-sm space-y-1.5" data-testid="approval-review-detail">
          <Row k="Target" v={req.target_label} />
          <Row k="Requested by" v={`${req.requested_by_name || "—"} (${req.requested_by_role || "—"})`} />
          <Row k="Requested" v={fdt(req.created_at)} />
          <Row k="Expires" v={fdt(req.expires_at)} />
          {req.reason ? <Row k="Reason" v={req.reason} /> : null}
          {before.name ? <Row k="Name" v={before.name} /> : null}
          {before.mobile ? <Row k="Mobile" v={before.mobile} /> : null}
          {before.invoice_number ? <Row k="Invoice" v={before.invoice_number} /> : null}
          {before.amount != null ? <Row k="Amount" v={String(before.amount)} /> : null}
          {req.status !== "pending" && <Row k="Decision" v={req.status} />}
          {req.approved_by_name ? <Row k="Approved by" v={`${req.approved_by_name} · ${fdt(req.approved_at)}`} /> : null}
          {req.approval_note ? <Row k="Note" v={req.approval_note} /> : null}
          {req.rejected_by_name ? <Row k="Rejected by" v={`${req.rejected_by_name} · ${fdt(req.rejected_at)}`} /> : null}
          {req.rejection_reason ? <Row k="Rejection reason" v={req.rejection_reason} /> : null}
        </div>

        {pending ? (
          rejecting ? (
            <div className="space-y-2">
              <Textarea value={rejReason} onChange={(e) => setRejReason(e.target.value)} data-testid="approval-reject-reason"
                placeholder="Why are you rejecting this request? (optional)" rows={3} className="bg-white" />
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setRejecting(false)} className="min-h-[44px]">Back</Button>
                <Button onClick={() => reject.mutate()} disabled={busy} data-testid="approval-reject-confirm"
                  className="min-h-[44px] bg-rose-600 hover:bg-rose-700 text-white">{reject.isPending ? "Rejecting…" : "Reject Request"}</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">Approving runs this action through Pawkt's existing data-protection rules. Add an optional note for the audit trail.</p>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="approval-approve-note"
                placeholder="Optional note (e.g. verified with customer)" rows={2} className="bg-white" />
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setRejecting(true)} data-testid="approval-reject-btn" className="min-h-[44px] text-rose-600 border-rose-200 hover:bg-rose-50">Reject</Button>
                <Button onClick={() => approve.mutate()} disabled={busy} data-testid="approval-approve-btn"
                  className="min-h-[44px] bg-emerald-600 hover:bg-emerald-700 text-white">{approve.isPending ? "Approving…" : "Approve"}</Button>
              </DialogFooter>
            </div>
          )
        ) : (
          <DialogFooter><Button onClick={onClose} className="min-h-[44px]">Close</Button></DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-3"><span className="text-slate-500">{k}</span><b className="text-slate-900 text-right truncate max-w-[60%]">{v}</b></div>
);
